/**
 * Empty state for a conversation with no messages yet.
 *
 * Replaces a centred headline plus two navigation cards. The cards were the
 * problem: they looked like the primary action but only changed page, so the
 * one thing the screen existed for — asking a question — had no on-ramp. Now
 * the prompts ARE questions, the first few built from the user's own workspace,
 * and navigation is demoted to a quiet row at the bottom.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../i18n';
import { buildSuggestions, summariseWorkspace } from '../utils/suggestions';

/** State-free routes only — the labeling studios need router state. */
const QUICK_LINKS = [
  { id: 'projects', icon: 'fa-folder', path: '/ProjectManagement', labelKey: 'assistant.quick.projects' },
  { id: 'models', icon: 'fa-cube', path: '/ModelManagement', labelKey: 'assistant.quick.models' },
  { id: 'video', icon: 'fa-image', path: '/VideoToImages', labelKey: 'assistant.quick.video' },
  { id: 'hub', icon: 'fa-layer-group', path: '/ModelHub', labelKey: 'assistant.quick.hub' },
  { id: 'predict', icon: 'fa-crosshairs', path: '/Prediction', labelKey: 'assistant.quick.predict' },
];

const firstName = () => {
  try {
    const raw = (localStorage.getItem('name') || '').trim();
    if (!raw) return '';
    return raw.split(/\s+/)[0];
  } catch (error) {
    return '';
  }
};

const AssistantWelcome = ({ workspace, workspaceLoading, onPick }) => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const name = firstName();
  const suggestions = buildSuggestions({ workspace, t });
  const totals = summariseWorkspace(workspace);
  const hasWorkspace = totals.versions > 0;

  return (
    <div className="lf-welcome">
      <div className="lf-welcome__intro">
        <span className="lf-welcome__mark" aria-hidden="true">
          <i className="fa fa-robot" />
        </span>
        <h1 className="lf-welcome__title">
          {name
            ? t('assistant.welcome.titleNamed', { name })
            : t('assistant.welcome.title')}
        </h1>
        <p className="lf-welcome__subtitle">{t('assistant.welcome.subtitle')}</p>
      </div>

      {/* A one-line read on what the assistant can actually see. Without it, a
          grounded answer quoting real image counts looks like a lucky guess. */}
      {hasWorkspace && (
        <p className="lf-welcome__context" role="status">
          <i className="fa fa-database" aria-hidden="true" />{' '}
          {t('assistant.welcome.contextLine', {
            projects: totals.projects,
            models: totals.models,
            images: totals.images.toLocaleString(),
          })}
        </p>
      )}
      {workspaceLoading && !hasWorkspace && (
        <p className="lf-welcome__context lf-welcome__context--loading" role="status">
          <i className="fa fa-spinner fa-spin" aria-hidden="true" /> {t('common.loading')}
        </p>
      )}

      <div className="lf-welcome__suggestions" role="list">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion.id}
            type="button"
            role="listitem"
            className={`lf-suggest${suggestion.kind === 'context' ? ' lf-suggest--context' : ''}`}
            onClick={() => onPick?.(suggestion.prompt)}
          >
            <span className="lf-suggest__icon" aria-hidden="true">
              <i className={`fa ${suggestion.icon}`} />
            </span>
            <span className="lf-suggest__text">
              <span className="lf-suggest__label">{suggestion.label}</span>
              <span className="lf-suggest__prompt">{suggestion.prompt}</span>
            </span>
            {suggestion.kind === 'context' && (
              <span className="lf-suggest__tag">{t('assistant.badge.yourData')}</span>
            )}
          </button>
        ))}
      </div>

      <nav className="lf-welcome__links" aria-label={t('assistant.quick.heading')}>
        <span className="lf-welcome__links-label">{t('assistant.quick.heading')}</span>
        {QUICK_LINKS.map((link) => (
          <button
            key={link.id}
            type="button"
            className="lf-welcome__link"
            onClick={() => navigate(link.path)}
          >
            <i className={`fa ${link.icon}`} aria-hidden="true" />
            {t(link.labelKey)}
          </button>
        ))}
      </nav>
    </div>
  );
};

export default AssistantWelcome;
